import { createError, defineNuxtRouteMiddleware, navigateTo, showError, useError, useNuxtApp, useRequestEvent } from 'nuxt/app';
import globalMiddlewareHeaders from '~~/common/server/globalMiddlewareHeaders';
import { extractResourceFromPath, RESOURCE_TYPE_REDIRECT } from '~~/common/utils/urlResource';
import { wouldCauseRedirectLoop } from '~~/common/utils/fixDoubleSlash';
import { isApiError } from '~~/common/utils/apiError';
import { getSeoModuleFromUrlResource } from '~~/common/utils/seoModule';
import { useAwRequestTracing } from '~~/common/composables/useAwRequestTracing';
import { SEO_MODULE_CMS, SEO_MODULE_GLOBAL } from '~~/common/config/SeoConfig';
import { useNotificationStore } from '~~/common/stores/notification';
import { useSeoStore } from '~~/common/stores/seo';
import { useContentsStore } from '~~/common/stores/contents';
import { useUserStore } from '~~/common/stores/user';
import { useUserInterfaceStore } from '~~/common/stores/userInterface';
import { COOKIE_TOKEN_EXPIRED, handleRedirectAfterTokenExpired, MSG_CATALOG_NOT_FOUND, MSG_PRODUCT_REDIRECT, MSG_SHORTLIST_NOT_FOUND, setCookieMsg } from '~~/common/server/globalMiddlewareConstants';
import { useUrlResourceStore } from '~~/common/stores/urlResource';
import { useConfigStore } from '~~/common/stores/config';
import { CAREER_PAGES_TURNED_OFF } from '~~/common/config/CareerTurnedOffConfig';

export default defineNuxtRouteMiddleware(async (to) => {
  const nuxtApp = useNuxtApp();
  const {
    $awt,
    $logger,
    $cookies,
    $pinia,
    $i18n,
    $router,
    $config,
    $localePath,
    $urlParts,
    $getHost,
  } = nuxtApp;
  const requestEvent = useRequestEvent();
  const requestId = useAwRequestTracing().getFeServerRequestId();
  const res = requestEvent?.node?.res;

  const notificationStore = useNotificationStore($pinia);
  const userStore = useUserStore($pinia);
  const seoStore = useSeoStore($pinia);
  const contentsStore = useContentsStore($pinia);
  const urlResourceStore = useUrlResourceStore($pinia);
  const userInterfaceStore = useUserInterfaceStore($pinia);
  const configStore = useConfigStore($pinia);
  const isCareerTurnOffApiControlEnabled = $config.public.isCareerTurnOffApiControlEnabled;
  const careerPageShutdown = configStore.configCommon?.persistentConfig?.career_page_shutdown;
  if (isCareerTurnOffApiControlEnabled && careerPageShutdown && to.path.startsWith('/karrier')) {
    const error = {
      statusCode: 422,
      statusText: CAREER_PAGES_TURNED_OFF,
      fatal: true,
    };
    showError(error);
    throw createError(error);
  }

  // NOTE: to.meta.layoutTransition has to be disabled for the error page.
  // It caused a layout duplication error when navigating to the error page
  // on the client side.
  if (useError().value) {
    to.meta.layoutTransition = false;
  }

  if (res && typeof res.setHeader === 'function') {
    for (const [headerKey, headerValue] of Object.entries(globalMiddlewareHeaders)) {
      if (headerValue === null) {
        res.removeHeader(headerKey);
      } else {
        res.setHeader(headerKey, headerValue);
      }
    }
  }

  if (userStore.forceLogoutToRedirect) {
    handleRedirectAfterTokenExpired($i18n, $cookies, navigateTo);
  } else if ($cookies.get(COOKIE_TOKEN_EXPIRED)) {
    $cookies.remove(COOKIE_TOKEN_EXPIRED);
    notificationStore.pushError({
      text: {
        title: $awt('aw.token_expired'),
      },
    });
  }

  const redirectData = urlResourceStore.redirectData;
  if (redirectData) {
    const { fromUrl, slug, type } = redirectData;
    if (type === RESOURCE_TYPE_REDIRECT && slug && fromUrl !== slug) {
      const { link, isOuter } = $urlParts(slug);
      const redirectUrl = $localePath(link);
      urlResourceStore.setRedirectData(null);

      if (wouldCauseRedirectLoop($getHost() || '', redirectUrl)) {
        $logger.error(`Aborting redirect from "${fromUrl}" to "${redirectUrl}": the target contains consecutive slashes, which would cause an infinite redirect loop`);
        throw createError({
          statusCode: 500,
          statusMessage: 'Redirect loop detected',
          fatal: true,
        });
      }

      return navigateTo({ path: redirectUrl }, { redirectCode: 301, external: isOuter });
    }
  }

  setCookieMsg({
    nuxtApp,
    cb (redirectMsg) {
      const messages: Record<string, string | undefined> = {
        [MSG_CATALOG_NOT_FOUND]: $awt('aw.cms.catalog.not_found'),
        [MSG_SHORTLIST_NOT_FOUND]: $awt('aw.shortlist.not_found'),
      };
      for (const msgKey in redirectMsg) {
        const msgParams = redirectMsg[msgKey];
        if (msgParams
            // To show msges when we redirect the user we have to store the
            // requestId in cookie so that the page where the redirection
            // leads to can show the message.
            && (import.meta.server
              ? (msgParams.requestId !== requestId)
              : true
            )
        ) {
          // eslint-disable-next-line @typescript-eslint/no-dynamic-delete -- redirectMsg is a dynamic cookie map; the consumed key must be removed
          delete redirectMsg[msgKey];
          if (msgKey === MSG_PRODUCT_REDIRECT) {
            notificationStore.pushError({
              text: {
                title: $awt('aw.common.product.redirect_notification.title'),
                subtitle: $awt('aw.common.product.redirect_notification.subtitle'),
              },
              manualClose: true,
            });
          } else if (messages[msgKey]) {
            notificationStore.pushError({
              text: {
                title: messages[msgKey],
              },
            });
          }
        }
      }
    },
  });

  let is404PageRequested = false;
  try {
    const langlessPath = to.path.replace(
      new RegExp(
        `^(${$i18n.localeCodes.value.map(lc => '/' + lc).join('|')})($|/)`,
      ),
      '/',
    );
    const cmsPage = [
      {
        regex: /^\/$/,
        staticContentType: 'dh_homepage',
      },
      {
        regex: /^\/shop(\/)*$/,
        staticContentType: 'homepage',
      },
      {
        regex: /^\/admin\/cms\/content_preview\/([a-zA-Z0-9_-]+)$/,
        staticContentType: '',
      },
    ].find(cmsPageConfig => cmsPageConfig.regex.test(langlessPath));
    if (cmsPage) {
      const contentPreviewHash = langlessPath.match(cmsPage.regex)?.[1];
      try {
        const STATIC_CONTENT_TYPE = cmsPage.staticContentType;
        if (STATIC_CONTENT_TYPE) {
          await contentsStore.fetchStaticContent({ type: STATIC_CONTENT_TYPE });
          if (contentsStore.staticContent[STATIC_CONTENT_TYPE]?.id) {
            await seoStore.fetchSeoData({
              seoModule: SEO_MODULE_CMS,
              id: contentsStore.staticContent[STATIC_CONTENT_TYPE].id,
            });
          }
        } else if (contentPreviewHash) {
          try {
            await contentsStore.fetchContentPreview({ hash: contentPreviewHash });
            await seoStore.fetchSeoData({
              seoModule: SEO_MODULE_CMS,
              id: contentsStore.previewData.id,
            });
          } catch (err) {
            if (isApiError(err) && err.response?.status === 404) {
              is404PageRequested = true;
            } else {
              $logger.error(err);
            }
          }
        }
      } catch (error) {
        if (!isApiError(error) || error.response?.status !== 404) {
          $logger.error(error);
        }
      }
    } else {
      const urlResource = extractResourceFromPath(to.fullPath);
      const seoModule = getSeoModuleFromUrlResource(urlResource, urlResourceStore, to, $i18n);
      const needFetchSeo = !(urlResource?.resourceType && ['shortlist', 'store', 'petrol'].includes(urlResource.resourceType));
      await Promise.allSettled([
        needFetchSeo ? seoStore.fetchSeoData(seoModule) : Promise.resolve(),
        (async () => {
          if (
            seoModule.seoModule === SEO_MODULE_CMS
              && seoModule.id
              && urlResourceStore?.data?.seoResourceId === seoModule.id
              && urlResourceStore.data.resourceId
          ) {
            await contentsStore.fetchContent({ pageId: urlResourceStore.data.resourceId });
            userInterfaceStore.setBackground({
              backgroundRepeat: contentsStore.data.backgroundRepeat ?? null,
              backgroundColor: contentsStore.data.backgroundColor ?? null,
              media: contentsStore.data.media ?? null,
            });
          } else if (seoModule.seoModule === SEO_MODULE_GLOBAL &&
              seoModule.id === -1 &&
              urlResourceStore?.data?.seoResourceId &&
              urlResourceStore.data.resourceId &&
              langlessPath === `/shop/${urlResourceStore?.data.slug}`
          ) {
            $router.replace(`/${urlResourceStore?.data.slug}`);
          }
        })(),
      ]);
    }
  } catch (err) {
    $logger.error(err);
  }
  if (is404PageRequested) {
    throw createError({
      statusCode: 404,
      fatal: true,
    });
  }
});
